import { StrictMode, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Cookies from 'js-cookie';
import classNames from 'classnames';
import { missing_hubspot_cookie, not_company_email, safe_json_parse, track_form_submission } from '@/components/forms/utils';
import { lookup } from '@/components/forms/l10n';


function ContactForm() {
  const url = new URL(document.URL);
  const page_lang = url.pathname.split('/')[1];
  const [sent, setSent] = useState(false);
  const [status, setStatus] = useState(lookup(page_lang, 'please_wait'));
  const [help, setHelp] = useState(lookup(page_lang, 'help'));
  const [showHelp, setShowHelp] = useState(false);

  const statusClass = classNames(
    'form--dark',
    'form-status',
    {
      'hidden': !sent
    });

  const helpClass = classNames(
    'form--dark',
    'form-status',
    {
      'hidden': !showHelp
    });

  const formClass = classNames(
    'form',
    'form--dark',
    {
      'hidden': sent
    });

  async function send(formData) {
    const jsonObject = Object.fromEntries(formData);
    const jsonString = JSON.stringify(jsonObject);
    const csrftoken = Cookies.get('csrftoken');
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('X-CSRFToken', csrftoken);
    try {
      const response = await fetch('/api/forms/contact/', {
        method: 'POST',
        body: jsonString,
        headers: headers
      });
      if (response.status === 200) {
        track_form_submission(window, document.title, document.URL);
        setStatus(lookup(page_lang, 'thanks_for_contacting'));
      }
      else {
        const data = await safe_json_parse(response);
        if (missing_hubspot_cookie(data)) {
          setStatus(lookup(page_lang, 'hs_cookies'));
        }
        else if (not_company_email(data)) {
          setHelp(lookup(page_lang, 'use_company_email'));
          setShowHelp(true);
          setSent(false);
        }
        else {
          setStatus(lookup(page_lang, 'error'));
        }
      }
    }
    catch (error) {
      console.error(`Error sending data: ${error.message}`);
      setStatus(lookup(page_lang, 'error'));
    }
  }

  function submit(e) {
    e.preventDefault();
    setSent(true);
    setShowHelp(false);
    const form = document.getElementById('contact-form');
    const formData = new FormData(form);
    formData.set('page_name', document.title);
    formData.set('page_uri', document.URL);
    send(formData);
  }
  return (
    <>
      <div className={statusClass}>
        <h2 className="form__title title-400 title-medium">{status}</h2>
      </div>
      <div className={helpClass}>
        <h2 className="form__title title-400 title-medium">{help}</h2>
      </div>
      <form className={formClass} onSubmit={submit} id='contact-form'>
        <div className="form__row">
          <div className="form__col-50">
            <div className="form-floating">
              {/* eslint-disable-next-line @stylistic/js/max-len */}
              <input name='first_name' type="text" className="form-control" id="contact-form-first-name" placeholder="first name" autoComplete="given-name" required />
              <label htmlFor="contact-form-first-name">{lookup(page_lang, 'first_name')}</label>
            </div>
          </div>
          <div className="form__col-50">
            <div className="form-floating">
              {/* eslint-disable-next-line @stylistic/js/max-len */}
              <input name='last_name' type="text" className="form-control" id="contact-form-last-name" placeholder="last name" autoComplete="family-name" required />
              <label htmlFor="contact-form-last-name">{lookup(page_lang, 'last_name')}</label>
            </div>
          </div>
        </div>
        <div className="form-floating">
          <input name='email' type="email" className="form-control" id="contact-form-email" placeholder="name@example.com" autoComplete="email" required />
          <label htmlFor="contact-form-email">{lookup(page_lang, 'company_email')}</label>
        </div>
        <div className="form__row">
          <div className="form__col-50">
            <div className="form-floating">
              {/* eslint-disable-next-line @stylistic/js/max-len */}
              <input name='job_title' type="text" className="form-control" id="contact-form-job-title" placeholder="job title" autoComplete="organization-title" required />
              <label htmlFor="contact-form-job-title">{lookup(page_lang, 'job_title')}</label>
            </div>
          </div>
          <div className="form__col-50">
            <div className="form-floating">
              {/* eslint-disable-next-line @stylistic/js/max-len */}
              <input name='company' type="text" className="form-control" id="contact-form-company-name" placeholder="company name" autoComplete="organization" required />
              <label htmlFor="contact-form-company-name">{lookup(page_lang, 'company_name')}</label>
            </div>
          </div>
        </div>
        <div className="form-floating">
          <textarea name='message' type="text" className="form-control" id="contact-form-message" placeholder="message" autoComplete="off" required />
          <label htmlFor="contact-form-message">{lookup(page_lang, 'your_message')}</label>
        </div>
        <div className="form-check">
          <input className="form-check-input" type="checkbox" value="" id="contact-form-check" required />
          <label className="form-check-label" htmlFor="contact-form-check" dangerouslySetInnerHTML={{ __html: lookup(page_lang, 'agree_privacy') }}></label>
        </div>
        <button className="form__button form__submit">
          {/* eslint-disable-next-line @stylistic/js/max-len */}
          <svg width="24" height="24" viewBox="0 0 24 24" fill="black" xmlns="http://www.w3.org/2000/svg"><path d="M17.92 11.6199C17.8724 11.4972 17.801 11.385 17.71 11.2899L12.71 6.28994C12.6168 6.1967 12.5061 6.12274 12.3842 6.07228C12.2624 6.02182 12.1319 5.99585 12 5.99585C11.7337 5.99585 11.4783 6.10164 11.29 6.28994C11.1968 6.38318 11.1228 6.49387 11.0723 6.61569C11.0219 6.73751 10.9959 6.86808 10.9959 6.99994C10.9959 7.26624 11.1017 7.52164 11.29 7.70994L14.59 10.9999H7C6.73478 10.9999 6.48043 11.1053 6.29289 11.2928C6.10536 11.4804 6 11.7347 6 11.9999C6 12.2652 6.10536 12.5195 6.29289 12.707C6.48043 12.8946 6.73478 12.9999 7 12.9999H14.59L11.29 16.2899C11.1963 16.3829 11.1219 16.4935 11.0711 16.6154C11.0203 16.7372 10.9942 16.8679 10.9942 16.9999C10.9942 17.132 11.0203 17.2627 11.0711 17.3845C11.1219 17.5064 11.1963 17.617 11.29 17.7099C11.383 17.8037 11.4936 17.8781 11.6154 17.9288C11.7373 17.9796 11.868 18.0057 12 18.0057C12.132 18.0057 12.2627 17.9796 12.3846 17.9288C12.5064 17.8781 12.617 17.8037 12.71 17.7099L17.71 12.7099C17.801 12.6148 17.8724 12.5027 17.92 12.3799C18.02 12.1365 18.02 11.8634 17.92 11.6199Z" /></svg>
          <span>{lookup(page_lang, 'send')}</span>
        </button>
      </form>
    </>
  );
}


export default function createContactForm() {
  const domNode = document.getElementById('contact-form-parent');
  if (domNode) {
    const root = createRoot(domNode);
    root.render(
      <StrictMode>
        <ContactForm />
      </StrictMode>
    );
  }
}
